/* Vault viewer (Content → Vaults → View). A full-screen grid of the vault's live
   media with filters, a 380px item side panel, and the analysis animation: tiles
   being analysed — by the vault's run or a single-item Analyse — get a scanning
   overlay. Both states are server-owned (the run row / of_content.analysis_status),
   so the animation picks up again after a reload; the grid polls the DB-only
   status endpoint to flip items to analysed as they land. */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../../../api/client';
import type { ContentFlagsBody, OnDemandVault, VaultAnalysisStatus, VaultContentItem } from '../../../../api/client';
import { useAuth } from '../../../../context/AuthContext';
import { useSalesCatalogue } from '../../SalesLadder/useSalesCatalogue';
import { fmtDuration } from '../../SalesLadder/MediaBadge';
import { VaultThumb } from '../../Sales/VaultThumb';
import { Btn, Card, ErrorLine, Icon, Loading, RunTag, Scan, Seg, Spin, Toggle } from '../kit';
import { font } from '../style';
import { C } from '../tokens';
import { MediaPicker } from '../MediaPicker';
import { ThumbStrip } from '../ThumbStrip';
import { useResolveIds } from '../resolveIds';
import { PinnedPrice, TagPeople, Classification, PanelRow } from './ItemPanel';

const PAGE = 48;
const POLL_MS = 4000;

type Filter = 'all' | 'active' | 'preview' | 'retired' | 'unanalysed';
type Sort = 'recent' | 'oldest' | 'unanalysed';

const isVid = (it: VaultContentItem) => (it.type || '').toLowerCase().includes('video') || (it.type || '').toLowerCase().includes('gif');
const stateOf = (it: VaultContentItem): Exclude<Filter, 'all'> =>
  !it.classified ? 'unanalysed' : it.is_unused ? 'retired' : (it.preview_content_ids ?? []).length ? 'preview' : 'active';

function flagsBody(it: VaultContentItem, p: Partial<ContentFlagsBody>): ContentFlagsBody {
  return {
    preview_content_ids: p.preview_content_ids ?? it.preview_content_ids ?? [],
    is_preview_content: p.is_preview_content ?? it.is_preview_content ?? false,
    is_unused: p.is_unused ?? it.is_unused ?? false,
    tagged_user_ids: p.tagged_user_ids ?? it.tagged_user_ids ?? [],
  };
}

/** Overlay DB-side fields from a status/classify echo, keeping the live media urls. */
const merge = (it: VaultContentItem, u: VaultContentItem): VaultContentItem => ({ ...it, ...u, type: it.type, thumb_url: it.thumb_url, full_url: it.full_url, duration: it.duration });

export function VaultViewer({ accountId, vault, status, initialSel, onClose, onChanged, onCredits }: {
  accountId: string;
  vault: OnDemandVault;
  /** Open with this item's side panel showing (once it's in the loaded page). */
  initialSel?: string;
  status?: VaultAnalysisStatus;
  onClose: () => void;
  onChanged: () => void;
  onCredits: (n: number) => void;
}) {
  const { t } = useTranslation('assistantCompact');
  const { isSuperAdmin } = useAuth();
  const [items, setItems] = useState<VaultContentItem[]>([]);
  const [next, setNext] = useState(0);
  const [more, setMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [sort, setSort] = useState<Sort>('recent');
  const [sel, setSel] = useState<string | null>(initialSel ?? null);
  const [picking, setPicking] = useState<string | null>(null);
  const [classifying, setClassifying] = useState<Set<string>>(new Set());
  const cat = useSalesCatalogue(accountId);

  const load = useCallback(async (offset: number) => {
    if (offset === 0) setLoading(true); else setLoadingMore(true);
    setErr(null);
    try {
      const p = await api.getVaultContent(accountId, vault.id, offset, PAGE);
      setItems((prev) => (offset === 0 ? p.items : [...prev, ...p.items]));
      setMore(p.has_more);
      setNext(p.next_offset);
    } catch (e) {
      setErr(e instanceof Error ? e.message : t('content.loadFailed'));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [accountId, vault.id, t]);
  useEffect(() => { void load(0); }, [load]);

  const vaultRunning = !!status?.running;
  const scanning = useCallback((it: VaultContentItem) => !!it.analysing || classifying.has(it.id) || (vaultRunning && !it.classified), [classifying, vaultRunning]);
  const scanIds = useMemo(() => items.filter((it) => it.analysing || (vaultRunning && !it.classified)).map((i) => i.id), [items, vaultRunning]);

  // Poll the DB for items being analysed (by the run or a reloaded single-item run).
  const pollKey = scanIds.slice(0, 200).join(',');
  useEffect(() => {
    if (!pollKey) return;
    let off = false;
    const h = window.setInterval(async () => {
      try {
        const rows = await api.getContentAnalysisStatus(accountId, pollKey.split(','));
        if (off) return;
        const by = new Map(rows.map((r) => [r.id, r]));
        let changed = false;
        setItems((prev) => prev.map((it) => {
          const u = by.get(it.id);
          if (!u || (u.classified === it.classified && !!u.analysing === !!it.analysing)) return it;
          changed = true;
          return merge(it, u);
        }));
        if (changed) onChanged();
      } catch { /* next tick */ }
    }, POLL_MS);
    return () => { off = true; window.clearInterval(h); };
  }, [pollKey, accountId, onChanged]);

  // When the vault's run finishes, one last sweep catches the final items.
  const [wasRunning, setWasRunning] = useState(vaultRunning);
  useEffect(() => {
    if (wasRunning && !vaultRunning) {
      const ids = items.filter((i) => !i.classified).map((i) => i.id).slice(0, 200);
      if (ids.length) api.getContentAnalysisStatus(accountId, ids).then((rows) => {
        const by = new Map(rows.map((r) => [r.id, r]));
        setItems((prev) => prev.map((it) => (by.has(it.id) ? merge(it, by.get(it.id)!) : it)));
      }, () => {});
    }
    setWasRunning(vaultRunning);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vaultRunning]);

  const applyFlags = useCallback(async (it: VaultContentItem, p: Partial<ContentFlagsBody>) => {
    setItems((prev) => prev.map((i) => (i.id === it.id ? { ...i, ...p } : i)));
    try {
      const u = await api.updateContentFlags(accountId, it.id, flagsBody(it, p));
      setItems((prev) => prev.map((i) => (i.id === it.id ? merge(i, u) : i)));
      onChanged();
    } catch (e) {
      setItems((prev) => prev.map((i) => (i.id === it.id ? it : i)));
      setErr(e instanceof Error ? e.message : t('viewer.flagsFailed'));
    }
  }, [accountId, onChanged, t]);

  const classify = useCallback(async (it: VaultContentItem) => {
    setClassifying((s) => new Set(s).add(it.id));
    setErr(null);
    try {
      const u = await api.classifyContentItem(accountId, it.id, { vault_id: vault.id, type: it.type, thumb_url: it.thumb_url, full_url: it.full_url, duration: it.duration });
      setItems((prev) => prev.map((i) => (i.id === it.id ? merge(i, { ...u, analysing: false }) : i)));
      onChanged();
      api.getAnalysisStatus(accountId).then((r) => onCredits(r.remaining_generations), () => {});
    } catch (e) {
      setErr(e instanceof Error ? e.message : t('viewer.classifyFailed'));
    } finally {
      setClassifying((s) => { const n = new Set(s); n.delete(it.id); return n; });
    }
  }, [accountId, vault.id, onChanged, onCredits, t]);

  const shown = useMemo(() => {
    let l = items.filter((i) => filter === 'all' || stateOf(i) === filter);
    if (sort === 'oldest') l = [...l].reverse();
    if (sort === 'unanalysed') l = [...l].sort((a, b) => Number(a.classified) - Number(b.classified));
    return l;
  }, [items, filter, sort]);
  const selIt = items.find((i) => i.id === sel) ?? null;
  const pickIt = items.find((i) => i.id === picking) ?? null;
  const go = (d: number) => {
    if (!shown.length) return;
    const pos = Math.max(0, shown.findIndex((i) => i.id === sel));
    setSel(shown[(pos + d + shown.length) % shown.length]!.id);
  };

  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || picking) return;
      if (sel) setSel(null); else onClose();
    };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [sel, picking, onClose]);

  const counts = useMemo(() => {
    const c: Record<Filter, number> = { all: items.length, active: 0, preview: 0, retired: 0, unanalysed: 0 };
    items.forEach((i) => { c[stateOf(i)] += 1; });
    return c;
  }, [items]);
  const analysed = items.filter((i) => i.classified).length;
  const left = items.filter(scanning).length;
  const indexOf = (id: string) => items.findIndex((i) => i.id === id);
  const nameOf = (it: VaultContentItem) => `${isVid(it) ? t('viewer.video') : t('viewer.photo')} ${String(indexOf(it.id) + 1).padStart(2, '0')}`;
  const statusLabel = (it: VaultContentItem) => (scanning(it) ? t('viewer.analysing') : t(`viewer.f.${stateOf(it)}`));

  return createPortal(
    <div className="cpt-root" onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'color-mix(in srgb, var(--om-page) 72%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 1240, height: '100%', maxHeight: 860, display: 'flex', flexDirection: 'column', background: C.surface, border: `1px solid ${C.borderHi}`, borderRadius: 12, overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,.5)' }}>
        <div style={{ height: 48, flex: 'none', display: 'flex', alignItems: 'center', gap: 10, padding: '0 10px 0 16px', borderBottom: `1px solid ${C.divider}` }}>
          <Icon name="grid" size={15} color={C.purpleLt} />
          <span style={{ font: font(700, 15), color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{vault.vault_name || vault.id}</span>
          <span style={{ font: font(400, 12), color: C.muted, whiteSpace: 'nowrap' }}>{t('viewer.meta', { count: items.length, analysed })}</span>
          {left > 0 && <RunTag>{t('viewer.analysingLeft', { count: left })}</RunTag>}
          <span style={{ flex: 1 }} />
          <button type="button" onClick={onClose} title={t('picker.close')} style={{ width: 30, height: 30, border: 0, borderRadius: 6, background: 'transparent', color: C.sub, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="x" size={16} /></button>
        </div>
        <div className="cpt-layout" style={{ flex: 1, minHeight: 0, display: 'flex' }}>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', flexWrap: 'wrap' }}>
              <Seg value={filter} onChange={setFilter} options={(['all', 'active', 'preview', 'retired', 'unanalysed'] as Filter[]).map((f) => ({ id: f, label: t(`viewer.f.${f}`), n: counts[f] }))} />
              <span style={{ flex: 1 }} />
              <select value={sort} onChange={(e) => setSort(e.target.value as Sort)} style={{ height: 28, background: C.input, border: `1px solid ${C.border}`, borderRadius: 6, padding: '0 8px', color: C.text, font: font(500, 12), outline: 'none' }}>
                <option value="recent">{t('viewer.sortRecent')}</option>
                <option value="oldest">{t('viewer.sortOldest')}</option>
                <option value="unanalysed">{t('viewer.sortUnanalysed')}</option>
              </select>
              <Btn kind="ghost" icon="refresh-cw" onClick={() => void load(0)} disabled={loading}>{t('content.refresh')}</Btn>
            </div>
            <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '0 16px 16px' }}>
              {err && <div style={{ marginBottom: 10 }}><ErrorLine>{err}</ErrorLine></div>}
              {loading ? <Loading /> : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill,minmax(${sel ? 104 : 118}px,1fr))`, gap: 8 }}>
                    {shown.map((it) => {
                      const scan = scanning(it);
                      const st = stateOf(it);
                      const dim = !scan && (st === 'unanalysed' || st === 'retired');
                      const pv = (it.preview_content_ids ?? []).length;
                      const badge = scan ? t('viewer.analysing') : st === 'unanalysed' ? t('viewer.f.unanalysed') : st === 'retired' ? t('viewer.f.retired') : null;
                      return (
                        <div key={it.id} onClick={() => setSel(sel === it.id ? null : it.id)} style={{ position: 'relative', aspectRatio: '1', borderRadius: 8, overflow: 'hidden', cursor: 'pointer', background: C.stripe }}>
                          <div style={{ position: 'absolute', inset: 0, opacity: dim ? 0.5 : 1 }}><VaultThumb accountId={accountId} url={it.thumb_url} className="w-full h-full" /></div>
                          {scan && <Scan />}
                          <div style={{ position: 'absolute', inset: 0, borderRadius: 8, boxShadow: `inset 0 0 0 2px ${it.id === sel ? C.purple : 'transparent'}`, pointerEvents: 'none', zIndex: 2 }} />
                          {isVid(it) && <span style={{ position: 'absolute', left: 6, bottom: 6, zIndex: 2, padding: '2px 6px', borderRadius: 4, background: 'color-mix(in srgb, var(--om-page) 80%, transparent)', display: 'flex', alignItems: 'center', gap: 4, font: font(500, 10.5), color: C.text, whiteSpace: 'nowrap' }}><Icon name="play" size={9} />{fmtDuration(it.duration)}</span>}
                          {badge && <span style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', zIndex: 2, padding: '3px 8px', borderRadius: 99, background: 'color-mix(in srgb, var(--om-page) 80%, transparent)', font: font(500, 10.5), color: C.purplePale, whiteSpace: 'nowrap', pointerEvents: 'none' }}>{badge}</span>}
                          {it.pinned_price ? <span style={{ position: 'absolute', right: 6, bottom: 6, zIndex: 2, padding: '2px 6px', borderRadius: 4, background: C.purple, font: font(600, 10.5), color: '#fff', whiteSpace: 'nowrap' }}>${it.pinned_price}</span> : null}
                          <div style={{ position: 'absolute', top: 6, right: 6, zIndex: 3, display: 'flex', gap: 4 }}>
                            <TileBtn icon="eye" on={pv > 0} disabled={!it.classified} title={!it.classified ? t('viewer.analyseFirst') : pv ? t('viewer.editPreview', { count: pv }) : t('viewer.addPreview')} onClick={() => setPicking(it.id)} />
                            <TileBtn icon="eye-off" on={!!it.is_unused} disabled={!it.classified} title={!it.classified ? t('viewer.analyseFirst') : t('viewer.retire')} onClick={() => void applyFlags(it, { is_unused: !it.is_unused })} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {more && (
                    <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 14 }}>
                      <Btn kind="ghost" icon={loadingMore ? undefined : 'chevron-down'} disabled={loadingMore} onClick={() => void load(next)} style={{ color: C.purplePale }}>{loadingMore ? <Spin /> : null}{t('viewer.loadMore')}</Btn>
                    </div>
                  )}
                  {!shown.length && <div style={{ padding: 36, textAlign: 'center', border: `1px dashed ${C.border}`, borderRadius: 8, font: font(400, 12.5), color: C.muted }}>{items.length ? t('viewer.noneForFilter') : t('viewer.empty')}</div>}
                </>
              )}
            </div>
          </div>
          {selIt && (
            <aside className="cpt-viewer-aside" style={{ width: 380, flex: 'none', borderLeft: `1px solid ${C.divider}`, overflow: 'auto', padding: '12px 16px 20px', display: 'flex', flexDirection: 'column', gap: 12, boxSizing: 'border-box' }}>
              <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minWidth: 0 }}>
                  <span style={{ font: font(600, 14), color: C.text }}>{nameOf(selIt)}</span>
                  <span style={{ font: font(400, 11.5), color: C.sub }}>{statusLabel(selIt)}{isVid(selIt) && selIt.duration ? ` · ${fmtDuration(selIt.duration)}` : ''}</span>
                </div>
                <PanelBtn icon="chevron-left" title={t('viewer.prev')} onClick={() => go(-1)} />
                <PanelBtn icon="chevron-right" title={t('viewer.next')} onClick={() => go(1)} />
                <PanelBtn icon="x" title={t('viewer.collapse')} onClick={() => setSel(null)} bare />
              </div>
              <div style={{ flex: 'none', aspectRatio: '4/3', borderRadius: 8, overflow: 'hidden', background: C.stripe }}>
                <VaultThumb accountId={accountId} url={selIt.thumb_url} className="w-full h-full" objectFit="contain" />
              </div>
              <div style={{ flex: 'none' }}>
                <Card>
                  <PinnedPrice key={`pin-${selIt.id}`} accountId={accountId} item={selIt} onSaved={(p, f) => setItems((prev) => prev.map((i) => (i.id === selIt.id ? { ...i, pinned_price: p, pinned_floor: f } : i)))} />
                  <PanelRow label={t('viewer.retireLabel')} help={selIt.classified ? t('viewer.retireHelp') : t('viewer.analyseFirst')}>
                    <Toggle on={!!selIt.is_unused} disabled={!selIt.classified} onChange={(v) => void applyFlags(selIt, { is_unused: v })} />
                  </PanelRow>
                  <PreviewRow accountId={accountId} cat={cat} item={selIt} onOpen={() => setPicking(selIt.id)} />
                  <TagPeople key={`tag-${selIt.id}`} accountId={accountId} item={selIt} onChange={(ids) => void applyFlags(selIt, { tagged_user_ids: ids })} />
                </Card>
              </div>
              {isSuperAdmin && (
                <Classification
                  key={`cls-${selIt.id}`}
                  accountId={accountId}
                  item={selIt}
                  busy={scanning(selIt)}
                  onAnalyse={() => void classify(selIt)}
                  onSaved={(u) => setItems((prev) => prev.map((i) => (i.id === u.id ? merge(i, u) : i)))}
                />
              )}
            </aside>
          )}
        </div>
      </div>
      {pickIt && (
        <div onClick={(e) => e.stopPropagation()}>
          <MediaPicker
            accountId={accountId}
            title={t('viewer.pickTitle', { name: nameOf(pickIt) })}
            initialSelected={pickIt.preview_content_ids ?? []}
            excludeIds={[pickIt.id]}
            allowEmpty
            fit={(p, v) => ({ text: p + v ? t('viewer.pickHint') : t('viewer.pickEmpty'), tone: 'ok' })}
            onConfirm={(ids) => { void applyFlags(pickIt, { preview_content_ids: ids }); setPicking(null); }}
            onClose={() => setPicking(null)}
          />
        </div>
      )}
    </div>,
    document.body,
  );
}

function TileBtn({ icon, on, disabled, title, onClick }: { icon: string; on: boolean; disabled?: boolean; title: string; onClick: () => void }) {
  return (
    <button type="button" title={title} disabled={disabled} onClick={(e) => { e.stopPropagation(); if (!disabled) onClick(); }} style={{ width: 22, height: 22, border: 0, borderRadius: 5, background: on ? C.purple : 'color-mix(in srgb, var(--om-page) 72%, transparent)', color: on ? '#fff' : C.sub, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.45 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Icon name={icon} size={12} />
    </button>
  );
}

function PanelBtn({ icon, title, onClick, bare }: { icon: string; title: string; onClick: () => void; bare?: boolean }) {
  return (
    <button type="button" title={title} onClick={onClick} style={{ width: 26, height: 26, border: bare ? 0 : `1px solid ${C.borderHi}`, borderRadius: 6, background: 'transparent', color: C.sub, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Icon name={icon} size={14} />
    </button>
  );
}


function PreviewRow({ accountId, cat, item, onOpen }: { accountId: string; cat: ReturnType<typeof useSalesCatalogue>; item: VaultContentItem; onOpen: () => void }) {
  const { t } = useTranslation('assistantCompact');
  const ids = item.preview_content_ids ?? [];
  const resolving = useResolveIds(accountId, cat, ids);
  return (
    <PanelRow label={<>{t('viewer.previewMedia')} <span style={{ color: C.muted }}>{ids.length}</span></>} help={item.classified ? t('viewer.previewHelp') : t('viewer.analyseFirst')}>
      <ThumbStrip accountId={accountId} ids={ids} byId={cat.byId} size={26} loading={resolving} />
      <Btn kind="dashed" height={26} disabled={!item.classified} onClick={onOpen}>{ids.length ? t('viewer.edit') : t('viewer.addPreviewBtn')}</Btn>
    </PanelRow>
  );
}
